import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography
} from "@material-ui/core";
import { Alert, Pagination } from "@material-ui/lab";
import PageContainer from "@jumbo/components/PageComponents/layouts/PageContainer";
import { getMyAccess } from "services/adminAccess";
import reportCardService from "services/reportCard";
import objectionService from "services/reportCardObjection";

const statuses = { Pending: "در انتظار بررسی", Accepted: "پذیرفته شده", Rejected: "رد شده" };
const statusMenuProps = {
  getContentAnchorEl: null,
  anchorOrigin: { vertical: "bottom", horizontal: "right" },
  transformOrigin: { vertical: "top", horizontal: "right" },
  PaperProps: { dir: "rtl", style: { marginTop: 4, borderRadius: 8 } },
  MenuListProps: { dir: "rtl" }
};
const errorMessage = error => (error.response && error.response.data && error.response.data.msgFa) || "انجام عملیات ممکن نشد. دوباره تلاش کنید.";

export default function ReportCardObjections() {
  const { id } = useParams();
  return <Objections key={id} id={id} />;
}

function Objections({ id }) {
  const [access, setAccess] = useState(null);
  const [formAccess, setFormAccess] = useState(false);
  const [card, setCard] = useState(null);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchString, setSearchString] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editor, setEditor] = useState(null);
  const [editorError, setEditorError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const reload = () => setRefresh(value => value + 1);

  useEffect(() => {
    let active = true;
    setFormAccess(false);
    if (access) {
      getMyAccess("FormFilled")
        .then(permission => { if (active) setFormAccess(permission); })
        .catch(() => { if (active) setFormAccess(false); });
    }
    return () => { active = false; };
  }, [access]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setSearchString(searchInput.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    async function load() {
      try {
        const permission = await getMyAccess("ReportCard");
        if (!active) return;
        setAccess(permission);
        if (!permission) return;
        const [cardResponse, response] = await Promise.all([
          reportCardService.getById(id), objectionService.getAll(id, page, status, searchString)
        ]);
        if (!active) return;
        setCard(cardResponse.data);
        const pages = Math.max(1, response.data.totalPages);
        if (page > pages) { setPage(pages); return; }
        setRecords(response.data.docs);
        setTotalPages(pages);
        setTotalDocs(response.data.totalDocs);
      } catch (err) {
        if (active) setError(errorMessage(err));
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => { active = false; };
  }, [id, page, status, searchString, refresh]);

  const edit = async record => {
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const { data } = await objectionService.getById(record._id);
      setEditor({ ...data, adminStatement: data.adminStatement || "" });
      setEditorError("");
    } catch (err) { setError(errorMessage(err)); }
    finally { setBusy(false); }
  };
  const save = async event => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setEditorError("");
    try {
      await objectionService.update(editor._id, {
        adminStatement: editor.adminStatement.trim(), status: editor.status
      });
      setEditor(null);
      setSuccess("پاسخ و وضعیت اعتراض ذخیره شد.");
      reload();
    } catch (err) { setEditorError(errorMessage(err)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (busy || !deleteTarget) return;
    setBusy(true);
    setDeleteError("");
    try {
      await objectionService.remove(deleteTarget._id);
      setDeleteTarget(null);
      setSuccess("اعتراض حذف شد.");
      reload();
    } catch (err) { setDeleteError(errorMessage(err)); }
    finally { setBusy(false); }
  };

  return <PageContainer heading="اعتراض‌های کارنامه">
    <Box dir="rtl">
      {card && <Typography variant="h5" gutterBottom>{card.title}</Typography>}
      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}
      {access === false && <Alert severity="warning">شما به مدیریت کارنامه دسترسی ندارید.</Alert>}
      {access && <Box my={3} display="flex" flexWrap="wrap" style={{ gap: 16 }}>
        <Box style={{ flex: "1 1 280px", maxWidth: 480 }}>
          <TextField id="objection-user-search" fullWidth size="small" variant="outlined"
            label="جستجوی کاربر" placeholder="نام و نام خانوادگی، کد ملی یا کد پرسنلی"
            type="search" value={searchInput} disabled={busy} InputLabelProps={{ shrink: true }}
            onChange={event => setSearchInput(event.target.value)} />
        </Box>
        <Box style={{ width: 280, maxWidth: "100%" }}><TextField id="objection-status-filter" select fullWidth size="small" variant="outlined" label="وضعیت اعتراض" value={status}
        InputLabelProps={{ shrink: true }}
        SelectProps={{ displayEmpty: true, renderValue: value => statuses[value] || "همه", MenuProps: statusMenuProps }}
        disabled={busy} onChange={event => { setStatus(event.target.value); setPage(1); }}>
        <MenuItem value="">همه</MenuItem>
        {Object.entries(statuses).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
      </TextField></Box></Box>}
      {loading ? <Box p={5} textAlign="center"><CircularProgress aria-label="در حال دریافت اعتراض‌ها" /></Box>
        : access && !error && <Paper>
          <TableContainer><Table aria-label="اعتراض‌های کارنامه">
            <TableHead><TableRow><TableCell>کاربر</TableCell><TableCell>وضعیت</TableCell><TableCell>تاریخ ثبت</TableCell><TableCell>عملیات</TableCell></TableRow></TableHead>
            <TableBody>{records.length ? records.map(record => <TableRow key={record._id}>
              <TableCell>{record.owner ? <>
                <Typography>{record.owner.fullName || "بدون نام"}</Typography>
                <Typography variant="body2">کد ملی: {record.owner.nationalCode || "—"}</Typography>
                <Typography variant="body2">کد پرسنلی: {record.owner.personnelCode || "—"}</Typography>
              </> : "کاربر حذف شده"}</TableCell>
              <TableCell>{statuses[record.status]}</TableCell>
              <TableCell>{new Date(record.createdAt).toLocaleString("fa-IR")}</TableCell>
              <TableCell>
                <Button color="primary" disabled={busy} onClick={() => edit(record)}>بررسی و پاسخ</Button>
                <Button color="secondary" disabled={busy} onClick={() => {
                  setDeleteTarget(record); setDeleteError(""); setSuccess("");
                }}>حذف</Button>
              </TableCell>
            </TableRow>) : <TableRow><TableCell colSpan={4} align="center">اعتراضی یافت نشد.</TableCell></TableRow>}</TableBody>
          </Table></TableContainer>
          <Box p={2} display="flex" justifyContent="space-between" flexWrap="wrap">
            <Typography>{totalDocs} اعتراض</Typography>
            <Pagination page={page} count={totalPages} disabled={busy} onChange={(_, value) => setPage(value)} />
          </Box>
        </Paper>}
      <Dialog open={Boolean(deleteTarget)} onClose={() => { if (!busy) setDeleteTarget(null); }} fullWidth maxWidth="xs" aria-labelledby="delete-objection-title">
        <Box dir="rtl">
          <DialogTitle id="delete-objection-title">حذف اعتراض</DialogTitle>
          <DialogContent>
            {deleteError && <Alert severity="error">{deleteError}</Alert>}
            <Typography>اعتراض {deleteTarget && deleteTarget.owner && deleteTarget.owner.fullName} و پاسخ مدیر به‌طور کامل حذف شوند؟ این عملیات قابل بازگشت نیست.</Typography>
          </DialogContent>
          <DialogActions>
            <Button disabled={busy} onClick={() => setDeleteTarget(null)}>انصراف</Button>
            <Button color="secondary" variant="contained" disabled={busy} onClick={remove}>{busy ? "در حال حذف…" : "حذف کامل اعتراض"}</Button>
          </DialogActions>
        </Box>
      </Dialog>
      <Dialog open={Boolean(editor)} onClose={() => { if (!busy) setEditor(null); }} fullWidth maxWidth="sm">
        {editor && <form onSubmit={save} dir="rtl">
          <DialogTitle>بررسی و پاسخ به اعتراض</DialogTitle>
          <DialogContent>
            {editorError && <Alert severity="error">{editorError}</Alert>}
            <Typography variant="subtitle1">{editor.owner && editor.owner.fullName}</Typography>
            <Typography variant="body2">کد ملی: {(editor.owner && editor.owner.nationalCode) || "—"}</Typography>
            <Typography variant="body2">کد پرسنلی: {(editor.owner && editor.owner.personnelCode) || "—"}</Typography>
            <Box my={1} display="flex" flexWrap="wrap" alignItems="center">
              {editor.owner && (editor.owner._id || typeof editor.owner === "string") && <>
              <Button component={Link} color="primary" variant="outlined" target="_blank" rel="noopener noreferrer"
                to={`/reportcards/${id}/results?owner=${encodeURIComponent(editor.owner._id || editor.owner)}`}>
                مشاهده نتایج کاربر
              </Button>
              <Button component={Link} color="primary" target="_blank" rel="noopener noreferrer"
                to={`/reportcards/${id}/results?owner=${encodeURIComponent(editor.owner._id || editor.owner)}&edit=1`}>
                ویرایش نتیجه
              </Button>
              </>}
              {editor.hasFormFilled && editor.formFilled && <Button color="primary" disabled={!formAccess}
                {...(formAccess ? { component: Link, to: `/formFillDetails/${editor.formFilled}`, target: "_blank", rel: "noopener noreferrer" } : {})}>
                مشاهده پاسخ‌های آزمون
              </Button>}
            </Box>
            <Typography style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{editor.userStatement}</Typography>
            <Box my={2}><TextField id="objection-admin-reply" label="پاسخ مدیر" multiline minRows={5} fullWidth variant="outlined" value={editor.adminStatement}
              disabled={busy} inputProps={{ maxLength: 5000 }} onChange={event => setEditor({ ...editor, adminStatement: event.target.value })} /></Box>
            <TextField id="objection-edit-status" label="وضعیت اعتراض" select fullWidth variant="outlined" value={editor.status} disabled={busy}
              InputLabelProps={{ shrink: true }} SelectProps={{ MenuProps: statusMenuProps }}
              onChange={event => setEditor({ ...editor, status: event.target.value })}>
              {Object.entries(statuses).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </TextField>
          </DialogContent>
          <DialogActions><Button disabled={busy} onClick={() => setEditor(null)}>انصراف</Button>
            <Button type="submit" color="primary" variant="contained" disabled={busy}>{busy ? "در حال ذخیره…" : "ذخیره پاسخ"}</Button>
          </DialogActions>
        </form>}
      </Dialog>
    </Box>
  </PageContainer>;
}
